import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ProductGallery from '../components/ProductGallery';
import FragranceNotes from '../components/FragranceNotes';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import CartDrawer from '../components/CartDrawer';
import { Star, Minus, Plus, Truck, ShieldCheck, Sparkles, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCatalogue } from '../context/CatalogueContext';
import SizeSelector from '../components/SizeSelector';
import { DEFAULT_SIZE, isSizeAvailable } from '../constants/bottleSizes';
import { isOutOfStock, maxPurchaseQty, remainingStock } from '../lib/stock';
import { fbTrack } from '../lib/fbpixel';

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { products } = useCatalogue();
  const product = products.find(p => p.slug === slug);
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const soldOut = isOutOfStock(product);
  const maxQty = maxPurchaseQty(product);
  const left = remainingStock(product);
  const canAdd = isSizeAvailable(size) && !soldOut;

  useEffect(() => {
    if (soldOut) {
      setQty(1);
      return;
    }
    setQty((q) => Math.min(Math.max(1, q), maxQty || 1));
  }, [soldOut, maxQty]);

  useEffect(() => {
    if (!product) return;
    fbTrack('ViewContent', {
      content_name: product.name,
      content_ids: [product.id],
      content_type: 'product',
      value: product.price,
      currency: 'INR',
    });
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const goBack = () => {
    // Same as browser Back so home scroll/section restore runs
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <p className="font-serif-display text-3xl mb-4">Product not found</p>
          <Link to="/" className="underline text-sm">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const discount = Math.round(100 - (product.price / product.mrp) * 100);

  const qtyControls = (
    <div className="flex items-center border border-[#1A2332]/20 rounded-sm flex-shrink-0">
      <button
        type="button"
        onClick={() => setQty((q) => Math.max(1, q - 1))}
        className="p-3 touch-manipulation"
        aria-label="Decrease quantity"
      >
        <Minus className="w-4 h-4" />
      </button>
      <span className="w-8 text-center">{qty}</span>
      <button
        type="button"
        onClick={() => setQty((q) => Math.min(maxQty || 1, q + 1))}
        className="p-3 touch-manipulation"
        aria-label="Increase quantity"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );

  const stickyCta =
    typeof document !== 'undefined'
      ? createPortal(
          <div className="md:hidden fixed bottom-0 inset-x-0 z-[60] border-t border-[#E5DCC9] bg-[#F5EFE6] px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-2 shadow-[0_-8px_24px_rgba(0,0,0,0.12)]">
            <div className="flex items-center border border-[#1A2332]/20 rounded-sm flex-shrink-0">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="p-2.5 touch-manipulation"
                aria-label="Decrease quantity"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-7 text-center text-sm">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(maxQty || 1, q + 1))}
                className="p-2.5 touch-manipulation"
                aria-label="Increase quantity"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              type="button"
              disabled={!canAdd}
              onClick={() => canAdd && addItem(product, qty, size)}
              className="flex-1 min-w-0 bg-[#1A2332] text-[#F5EFE6] py-3 text-[11px] tracking-[0.15em] uppercase touch-manipulation disabled:opacity-40"
            >
              {soldOut ? 'Out of stock' : `Add to Cart — ₹${product.price * qty}`}
            </button>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="bg-[#F5EFE6] min-h-screen pb-28 md:pb-0">
      <div className="sticky top-0 z-50">
        <AnnouncementBar />
        <Header embedded />
      </div>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[#1A2332]/70 hover:text-[#1A2332]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 grid md:grid-cols-2 gap-6 md:gap-16 py-4 md:py-10">
        <ProductGallery product={product} />
        <div>
          <p className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[#1A2332]/60 mb-2">
            {product.code} • Extrait De Parfume • {size} ML
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#1A2332] mb-2">{product.name}</h1>
          <p className="font-serif-body italic text-base sm:text-lg text-[#1A2332]/70 mb-4">{product.tagline}</p>
          <div className="flex items-center gap-1 text-sm text-[#1A2332]/70 mb-5 sm:mb-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-4 h-4 fill-[#C9A961] text-[#C9A961]" />
            ))}
            <span className="ml-1">
              {product.rating} • {product.reviews} Reviews
            </span>
          </div>
          <div className="flex items-baseline gap-3 mb-5 sm:mb-6 flex-wrap">
            <span className="font-serif-display text-2xl sm:text-3xl text-[#1A2332]">₹{product.price}</span>
            <span className="text-base sm:text-lg line-through text-[#1A2332]/40">₹{product.mrp}</span>
            <span className="text-xs font-semibold text-[#8a4a2b]">{discount}% OFF</span>
          </div>

          <SizeSelector value={size} onChange={setSize} />

          {soldOut ? (
            <p className="text-sm text-[#8a4a2b] mb-4">This fragrance is currently out of stock.</p>
          ) : left != null && left <= 5 ? (
            <p className="text-sm text-[#8a4a2b] mb-4">Only {left} left.</p>
          ) : null}

          {/* Always visible in content (mobile + desktop) */}
          <div className="flex items-center gap-3 mb-6">
            {qtyControls}
            <button
              type="button"
              disabled={!canAdd}
              onClick={() => canAdd && addItem(product, qty, size)}
              className="flex-1 bg-[#1A2332] text-[#F5EFE6] py-3.5 text-[11px] sm:text-[12px] tracking-[0.2em] sm:tracking-[0.25em] uppercase hover:bg-[#2c3e5b] transition touch-manipulation disabled:opacity-40"
            >
              {soldOut ? 'Out of stock' : `Add to Cart — ₹${product.price * qty}`}
            </button>
          </div>

          <p className="text-sm sm:text-base text-[#1A2332]/80 leading-relaxed mb-6 sm:mb-8">{product.description}</p>

          <FragranceNotes product={product} />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-6 border-t border-[#E5DCC9]">
            <div className="flex items-start gap-2">
              <Truck className="w-5 h-5 text-[#1A2332]/60 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold">Free Shipping</p>
                <p className="text-[11px] text-[#1A2332]/60">Above ₹599</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-[#1A2332]/60 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold">100% Genuine</p>
                <p className="text-[11px] text-[#1A2332]/60">Long-lasting</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Sparkles className="w-5 h-5 text-[#1A2332]/60 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold">Free Sample</p>
                <p className="text-[11px] text-[#1A2332]/60">Above ₹2,499</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {stickyCta}

      <Footer />
      <CartDrawer />
    </div>
  );
}
